import { useState } from "preact/hooks";
import { quickplay } from "../../api/client";
import { GAME_MODES } from "../../api/modes";
import type { GameMode } from "../../api/types";
import { startGame } from "../../state/game";
import { quickplayModes, setQuickplayModes } from "../../state/matchmaking";
import { requireMeta } from "../../state/meta";
import { toastError } from "../../state/toasts";
import { Button } from "../components/Button";
import { OptionCard } from "../components/OptionCard";
import { ScreenFrame } from "../components/ScreenFrame";
import { MODE_BLURBS, describeRules } from "./modeInfo";
import { useAlive } from "./rooms/useAlive";

/** Quick play: tick the modes you are happy with, the server picks the best open room. */
export function Play() {
  const catalog = requireMeta();
  const selected = quickplayModes.value;
  const [busy, setBusy] = useState(false);
  const alive = useAlive();

  const toggle = (id: GameMode) => {
    setQuickplayModes(selected.includes(id) ? selected.filter((m) => m !== id) : [...selected, id]);
  };

  const play = async () => {
    if (busy || selected.length === 0) return;
    setBusy(true);
    try {
      const join = await quickplay(selected);
      // Leaving the screen while the request is in flight cancels the join.
      if (alive.current) startGame(join);
    } catch (e) {
      toastError(e);
      setBusy(false);
    }
  };

  const allSelected = selected.length === GAME_MODES.length;

  return (
    <ScreenFrame
      title="Play"
      kicker="Quick match"
      actions={
        <Button size="sm" variant="ghost" onClick={() => setQuickplayModes(allSelected ? [] : [...GAME_MODES])}>
          {allSelected ? "Clear all" : "Select all"}
        </Button>
      }
      footer={
        <>
          <p class="play__summary" aria-live="polite">
            {selected.length === 0
              ? "Pick at least one mode."
              : `${selected.length} of ${GAME_MODES.length} modes selected. You join the best open room within a second.`}
          </p>
          <Button variant="primary" size="lg" busy={busy} disabled={selected.length === 0} onClick={() => void play()}>
            Play
          </Button>
        </>
      }
    >
      <div class="play__modes" role="group" aria-label="Game modes">
        {catalog.modes.map((mode) => (
          <OptionCard
            key={mode.mode}
            role="checkbox"
            selected={selected.includes(mode.mode)}
            onClick={() => toggle(mode.mode)}
            class="play__mode"
          >
            <span class="card__title">{mode.name}</span>
            <span class="play__rules">{describeRules(mode.rules)}</span>
            <span class="card__meta">{MODE_BLURBS[mode.mode]}</span>
          </OptionCard>
        ))}
      </div>
    </ScreenFrame>
  );
}
